import { useTokensReader } from '#imports';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { ref } from 'vue';
import PearlApiService from '~/composables/PearlApiService';
import useEnvs from '~/composables/useEnvs';
import type { IPercentAPRResponse, ITokenModel } from '~/types/apiService';
import type { TNullable } from '~/types/common';
import { Tokens } from '~/utils/constants';

interface TokenData {
  tokenBalance: string;
  tokenStakedBalance: string;
  blackPearlBalance: string;
  blackPearlStakedBalance: string;
  totalStaked: string;
  stakedPercent: string;
  aprPercent: string;
  earnedBalance: string;
  currentTokenInfo: TNullable<ITokenModel>;
  isLoading: boolean;
  lastUpdated: number;
}

const tokensCache = new Map<string, Map<Tokens, TokenData>>();
const isPreloading = ref(false);

export default () => {
  const { address } = useWeb3ModalAccount();
  const { blockchain, apiUrl } = useEnvs();
  const { availableTokens, totalStake, staked, earned } = useTokensReader();

  const createEmptyTokenData = (): TokenData => ({
    tokenBalance: '',
    tokenStakedBalance: '0',
    blackPearlBalance: '0',
    blackPearlStakedBalance: '',
    totalStaked: '0',
    stakedPercent: '0',
    aprPercent: '',
    earnedBalance: '',
    currentTokenInfo: null,
    isLoading: false,
    lastUpdated: 0
  });

  const getAddressCache = (): Map<Tokens, TokenData> => {
    if (!address.value) {
      return new Map();
    }

    let addressCache = tokensCache.get(address.value);
    if (!addressCache) {
      addressCache = new Map<Tokens, TokenData>();
      tokensCache.set(address.value, addressCache);
    }
    return addressCache;
  };

  const loadTokenData = async (token: Tokens, silent = false): Promise<TokenData> => {
    if (!address.value) {
      return createEmptyTokenData();
    }

    const addressCache = getAddressCache();

    let cachedData = addressCache.get(token);
    if (!cachedData) {
      cachedData = createEmptyTokenData();
      addressCache.set(token, cachedData);
    }

    if (!silent) {
      cachedData.isLoading = true;
    }

    try {
      const secondaryTokenAddress =
        (token === Tokens.pearl
          ? blockchain.contracts.blackPearl.addresses.contract
          : blockchain.contracts[token].addresses.secondaryToken) || '';

      const [
        tokenBalanceValue,
        blackPearlBalanceValue,
        tokenStakedBalanceValue,
        blackPearlStakedBalanceValue,
        totalStakedValue,
        earnedBalanceValue,
        aprValue,
        tokenInfoValue
      ] = await Promise.allSettled([
        availableTokens(address.value, blockchain.contracts[token].addresses.contract),
        availableTokens(address.value, secondaryTokenAddress as string),
        staked(address.value, token),
        staked(address.value, Tokens.blackPearl),
        totalStake(token),
        earned(address.value, token),
        PearlApiService.getAprPercent(apiUrl, blockchain.contracts[token].addresses.contract),
        PearlApiService.getTokenByKey(apiUrl, token)
      ]).then((results) =>
        results.map((res) => {
          return res.status === 'fulfilled' ? res.value : null;
        })
      );

      const updatedData: TokenData = {
        currentTokenInfo: tokenInfoValue as TNullable<ITokenModel>,
        tokenBalance: tokenBalanceValue?.toString() ?? '',
        blackPearlBalance: blackPearlBalanceValue?.toString() ?? '0',
        tokenStakedBalance: tokenStakedBalanceValue ? tokenStakedBalanceValue.toString() : '0',
        blackPearlStakedBalance: blackPearlStakedBalanceValue?.toString() ?? '',
        totalStaked: totalStakedValue ? totalStakedValue?.toString() : '0',
        stakedPercent:
          BigNumber(totalStakedValue?.toString() || '0').isZero() ||
          BigNumber(totalStakedValue?.toString() || '0').isNaN()
            ? '0'
            : BigNumber(tokenStakedBalanceValue as string)
                .dividedBy(totalStakedValue?.toString() || '1')
                .multipliedBy(100)
                .toFixed(4)
                .toString(),
        aprPercent: (aprValue as IPercentAPRResponse)
          ? new BigNumber((aprValue as IPercentAPRResponse).apr ?? '0').toString()
          : '',
        earnedBalance: earnedBalanceValue?.toString() ?? '',
        isLoading: false,
        lastUpdated: Date.now()
      };

      addressCache.set(token, updatedData);

      return updatedData;
    } catch (err) {
      console.log('Error loading token data:', token, err);
      cachedData.isLoading = false;
      return cachedData;
    }
  };

  const preloadAllTokens = async (currentToken: Tokens, sellableTokens: Tokens[]) => {
    if (isPreloading.value || !address.value) {
      return;
    }

    isPreloading.value = true;

    await loadTokenData(currentToken, false);

    const tokensToPreload = sellableTokens.filter((token) => token !== currentToken);

    Promise.all(tokensToPreload.map((token) => loadTokenData(token, true)))
      .then(() => {
        console.log('All tokens preloaded successfully');
      })
      .catch((err) => {
        console.error('Error preloading tokens:', err);
      })
      .finally(() => {
        isPreloading.value = false;
      });
  };

  const getTokenData = async (token: Tokens, forceRefresh = false): Promise<TokenData> => {
    if (!address.value) {
      return createEmptyTokenData();
    }

    const addressCache = getAddressCache();
    const cached = addressCache.get(token);

    if (cached && !forceRefresh) {
      loadTokenData(token, true);
      return cached;
    }

    return loadTokenData(token, false);
  };

  const hasCache = (token: Tokens): boolean => {
    if (!address.value) return false;
    const addressCache = tokensCache.get(address.value);
    return addressCache ? addressCache.has(token) : false;
  };

  const getCachedData = (token: Tokens): TokenData | undefined => {
    if (!address.value) return undefined;
    const addressCache = tokensCache.get(address.value);
    return addressCache?.get(token);
  };

  return {
    loadTokenData,
    preloadAllTokens,
    getTokenData,
    hasCache,
    getCachedData,
    isPreloading
  };
};
